import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X, Clock, ArrowRight } from 'lucide-react'
import { searchCatalogue, type SearchResult } from '@/lib/api'
import { useClickOutside } from '@/hooks/useClickOutside'
import { useDebounce } from '@/hooks/useDebounce'
import { Image } from '@/components/ui/Image'
import { formatPrice } from '@/lib/utils'

const RECENT_KEY = 'little-sky-recent-searches'
const MAX_RECENT = 8

interface SearchOverlayProps {
  isOpen: boolean
  onClose: () => void
}

export function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')

  useClickOutside(panelRef, onClose, isOpen)
  const debounced = useDebounce(query, 220)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [recent, setRecent] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setResults([])
      setLoading(false)
      setRecent(loadRecent())
      // Focus after animation so the virtual keyboard on mobile behaves.
      const id = setTimeout(() => inputRef.current?.focus(), 120)
      return () => clearTimeout(id)
    }
  }, [isOpen])

  useEffect(() => {
    if (!debounced.trim()) {
      setResults([])
      return
    }
    setLoading(true)
    searchCatalogue(debounced, 12)
      .then((items) => {
        setResults(items)
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false))
  }, [debounced])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const goToProduct = (slug: string) => {
    addRecent(query)
    onClose()
    navigate(`/product/${slug}`)
  }

  const submitSearch = (q: string) => {
    if (!q.trim()) return
    addRecent(q)
    onClose()
    navigate(`/shop?search=${encodeURIComponent(q)}`)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[70] bg-paper/95 backdrop-blur-md"
        >
          <div ref={panelRef} className="mx-auto flex h-full max-w-4xl flex-col px-6 py-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-wd-orange" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSearch(query)
                  }}
                  placeholder="Search fragrances, families, notes..."
                  className="h-14 w-full border-b border-ink/10 bg-transparent pl-12 pr-4 text-lg text-carbon placeholder:text-carbon/40 focus:border-ink focus:outline-none"
                  aria-label="Search products"
                />
              </div>
              <button
                onClick={onClose}
                aria-label="Close search"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 text-carbon transition-colors hover:bg-oyster"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-8">
              {!debounced.trim() && (
                <div className="space-y-8">
                  {recent.length > 0 && (
                    <section>
                      <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-wd-orange">
                        Recent searches
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {recent.map((term) => (
                          <button
                            key={term}
                            onClick={() => {
                              setQuery(term)
                              submitSearch(term)
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/10 px-4 py-2 text-sm text-carbon transition-colors hover:bg-oyster"
                          >
                            <Clock className="h-3.5 w-3.5 text-wd-orange" />
                            {term}
                          </button>
                        ))}
                      </div>
                    </section>
                  )}

                  <section>
                    <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-wd-orange">
                      Popular categories
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {['Floral', 'Woody', 'Citrus', 'Oriental', 'Fresh', 'Gourmand'].map((family) => (
                        <button
                          key={family}
                          onClick={() => submitSearch(family)}
                          className="rounded-full border border-ink/10 px-4 py-2 text-sm text-carbon transition-colors hover:bg-oyster"
                        >
                          {family}
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {debounced.trim() && (
                <div className="space-y-6">
                  {loading ? (
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="space-y-3">
                          <div className="bg-stone" style={{ aspectRatio: '3/4' }} />
                          <div className="h-4 w-2/3 bg-stone" />
                          <div className="h-3 w-1/2 bg-stone" />
                        </div>
                      ))}
                    </div>
                  ) : results.length === 0 ? (
                    <div className="py-12 text-center">
                      <p className="text-carbon">No results for “{debounced}”.</p>
                      <button
                        onClick={() => submitSearch(debounced)}
                        className="mt-4 inline-flex items-center gap-2 text-sm text-wd-orange hover:underline"
                      >
                        Browse all fragrances <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3">
                      {results.map((product) => (
                        <button
                          key={product.slug}
                          onClick={() => goToProduct(product.slug)}
                          className="group text-left"
                        >
                          <Image
                            src={product.image}
                            alt={product.name}
                            aspectRatio="3/4"
                            containerClassName="mb-3 transition-transform duration-500 ease-out-expo group-hover:scale-[1.02]"
                          />
                          <p className="font-display text-sm normal-case text-carbon group-hover:text-wd-orange">
                            {product.name.toLowerCase()}
                          </p>
                          <p className="text-xs uppercase tracking-[0.12em] text-wd-orange">{product.family}</p>
                          <p className="mt-1 text-sm text-carbon">{formatPrice(product.price)}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function addRecent(term: string) {
  if (!term.trim()) return
  const next = [term, ...(loadRecent().filter((t) => t !== term))].slice(0, MAX_RECENT)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {}
}
